import React, { useMemo, useState } from 'react';
import { Card, Checkbox, Empty, Space, Typography } from 'antd';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    DailyBreakdown,
    DailyMemberBreakdown,
    EVENT_TYPES,
    EVENT_TYPE_LABELS,
    TeamMemberInsight,
} from './types';

interface Props {
    data: DailyBreakdown[];
    members: TeamMemberInsight[];
    /** Coder template display names — drives the Coder usage chart's metric series. */
    coderTemplates: string[];
}

const coderDashArrays = ['5 5', '2 2', '8 4', '10 2', '6 2'];

type ActivityMetricKey =
    | 'prsMerged'
    | 'prsActedOn'
    | 'issuesOpened'
    | 'issuesClosed'
    | 'issuesActedOn'
    | 'docsCreated'
    | 'demos'
    | 'locChanged';

type MetricDescriptor =
    | { key: string; label: string; dasharray?: string; kind: 'github'; field: ActivityMetricKey }
    | { key: string; label: string; dasharray?: string; kind: 'event'; eventType: string }
    // Coder active workspace time, plotted in hours. template === null → total across templates.
    | { key: string; label: string; dasharray?: string; kind: 'coder'; template: string | null };

const ACTIVITY_METRICS: MetricDescriptor[] = [
    { key: 'prsMerged', label: 'PRs merged', kind: 'github', field: 'prsMerged' },
    { key: 'prsActedOn', label: 'PRs acted on', dasharray: '5 5', kind: 'github', field: 'prsActedOn' },
    { key: 'issuesOpened', label: 'Issues opened', dasharray: '2 2', kind: 'github', field: 'issuesOpened' },
    { key: 'issuesClosed', label: 'Issues closed', dasharray: '8 4', kind: 'github', field: 'issuesClosed' },
    { key: 'issuesActedOn', label: 'Issues acted on', dasharray: '4 4 1 4', kind: 'github', field: 'issuesActedOn' },
    { key: 'docsCreated', label: 'Docs created', dasharray: '10 2', kind: 'github', field: 'docsCreated' },
    { key: 'demos', label: 'Demos', dasharray: '6 2', kind: 'github', field: 'demos' },
];

// Plotted in a dedicated chart block because LOC values are 1–3 orders of magnitude
// larger than the integer counts above and would flatten them on a shared Y axis.
const LOC_METRICS: MetricDescriptor[] = [
    { key: 'locChanged', label: 'Lines changed', kind: 'github', field: 'locChanged' },
];

// One series per known event type. Adding a new EventType to types.ts auto-adds a series.
const eventDashArrays = ['1 3', '6 2 1 2', '12 2', '3 3 6 3'];
const EVENT_METRICS: MetricDescriptor[] = EVENT_TYPES.map((type, idx) => ({
    key: `event_${type}`,
    label: EVENT_TYPE_LABELS[type],
    dasharray: eventDashArrays[idx % eventDashArrays.length],
    kind: 'event' as const,
    eventType: type as string,
}));

const readMetric = (member: DailyMemberBreakdown, m: MetricDescriptor): number => {
    if (m.kind === 'github') return member[m.field] ?? 0;
    if (m.kind === 'coder') {
        const secs = m.template === null ? member.coderActiveSeconds : member.coderTemplateSeconds?.[m.template];
        // Plot in hours (rounded to 2dp to avoid floating-point noise on the axis/tooltip).
        return Math.round(((secs ?? 0) / 3600) * 100) / 100;
    }
    return member.eventsByType?.[m.eventType] ?? 0;
};

// Ant Design v5 default palette — distinguishable for ~10 members before repeats.
const MEMBER_COLORS = [
    '#1677ff', '#52c41a', '#fa541c', '#722ed1', '#13c2c2',
    '#eb2f96', '#faad14', '#2f54eb', '#a0d911', '#fa8c16',
];

const colorFor = (memberIndex: number): string =>
    MEMBER_COLORS[memberIndex % MEMBER_COLORS.length];

const seriesKey = (login: string, metricKey: string): string => `${login}__${metricKey}`;

interface ChartBlockProps {
    title: string;
    metrics: MetricDescriptor[];
    selectedMetrics: string[];
    onMetricsChange: (next: string[]) => void;
    selectedMembers: string[];
    memberLogins: string[];
    data: DailyBreakdown[];
    /** Rendered below the chart in muted text. */
    caption?: React.ReactNode;
    emptyHint: string;
    /** Allow fractional Y-axis ticks (e.g. hours for Coder time). Counts stay integer. */
    allowDecimals?: boolean;
}

const ChartBlock: React.FC<ChartBlockProps> = ({
    title,
    metrics,
    selectedMetrics,
    onMetricsChange,
    selectedMembers,
    memberLogins,
    data,
    caption,
    emptyHint,
    allowDecimals,
}) => {
    const chartData = useMemo(() => {
        return data.map((day) => {
            const row: Record<string, string | number> = { date: day.date };
            for (const member of day.members) {
                for (const metric of metrics) {
                    row[seriesKey(member.login, metric.key)] = readMetric(member, metric);
                }
            }
            for (const login of memberLogins) {
                for (const metric of metrics) {
                    const key = seriesKey(login, metric.key);
                    if (!(key in row)) row[key] = 0;
                }
            }
            return row;
        });
    }, [data, memberLogins, metrics]);

    const activeSeries = useMemo(() => {
        const out: Array<{ key: string; name: string; color: string; dasharray?: string }> = [];
        for (const login of selectedMembers) {
            const memberIdx = memberLogins.indexOf(login);
            if (memberIdx < 0) continue;
            for (const metric of metrics) {
                if (!selectedMetrics.includes(metric.key)) continue;
                out.push({
                    key: seriesKey(login, metric.key),
                    name: `${login} · ${metric.label}`,
                    color: colorFor(memberIdx),
                    dasharray: metric.dasharray,
                });
            }
        }
        return out;
    }, [selectedMembers, selectedMetrics, memberLogins, metrics]);

    return (
        <Card size="small" style={{ marginTop: 16 }} title={<Typography.Text strong>{title}</Typography.Text>}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                        Metrics
                    </Typography.Text>
                    <Checkbox.Group
                        options={metrics.map((m) => ({ label: m.label, value: m.key }))}
                        value={selectedMetrics}
                        onChange={(vals) => onMetricsChange(vals as string[])}
                    />
                </div>

                {activeSeries.length === 0 || chartData.length === 0 ? (
                    <Empty description={emptyHint} />
                ) : (
                    <div style={{ width: '100%', height: 360 }}>
                        <ResponsiveContainer>
                            <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis allowDecimals={allowDecimals ?? false} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                {activeSeries.map((s) => (
                                    <Line
                                        key={s.key}
                                        type="monotone"
                                        dataKey={s.key}
                                        name={s.name}
                                        stroke={s.color}
                                        strokeDasharray={s.dasharray}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {caption && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {caption}
                    </Typography.Text>
                )}
            </Space>
        </Card>
    );
};

const TeamInsightsTimeline: React.FC<Props> = ({ data, members, coderTemplates }) => {
    const memberLogins = useMemo(() => members.map((m) => m.login), [members]);

    // Coder series: a "Total" line plus one per template. Rebuilt when the template set changes.
    const coderMetrics = useMemo<MetricDescriptor[]>(
        () => [
            { key: 'coder_total', label: 'Total', kind: 'coder', template: null },
            ...coderTemplates.map((tpl, idx) => ({
                key: `coder_tpl_${tpl}`,
                label: tpl,
                dasharray: coderDashArrays[idx % coderDashArrays.length],
                kind: 'coder' as const,
                template: tpl,
            })),
        ],
        [coderTemplates],
    );

    const [selectedMembers, setSelectedMembers] = useState<string[]>(memberLogins);
    const [selectedGithubMetrics, setSelectedGithubMetrics] = useState<string[]>(['prsMerged']);
    const [selectedLocMetrics, setSelectedLocMetrics] = useState<string[]>([]);
    const [selectedCoderMetrics, setSelectedCoderMetrics] = useState<string[]>(['coder_total']);
    const [selectedEventMetrics, setSelectedEventMetrics] = useState<string[]>(
        EVENT_METRICS.map((m) => m.key),
    );

    const memberSignature = memberLogins.join(',');
    React.useEffect(() => {
        const known = new Set(memberLogins);
        setSelectedMembers((prev) => {
            const filtered = prev.filter((m) => known.has(m));
            if (filtered.length === 0 && memberLogins.length > 0) return memberLogins;
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [memberSignature, memberLogins]);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small" style={{ marginTop: 16 }} title={<Typography.Text strong>Members</Typography.Text>}>
                <Checkbox.Group
                    options={memberLogins.map((m) => ({ label: m, value: m }))}
                    value={selectedMembers}
                    onChange={(vals) => setSelectedMembers(vals as string[])}
                />
            </Card>

            <ChartBlock
                title="Daily activity"
                metrics={ACTIVITY_METRICS}
                selectedMetrics={selectedGithubMetrics}
                onMetricsChange={setSelectedGithubMetrics}
                selectedMembers={selectedMembers}
                memberLogins={memberLogins}
                data={data}
                emptyHint="Select at least one member and one metric"
                caption={
                    <>
                        Line style encodes the metric (solid = PRs merged, dashed = PRs acted on, etc.).
                        Color encodes the member. Lines changed lives in its own chart below — its
                        scale would otherwise flatten these integer counts.
                    </>
                }
            />

            <ChartBlock
                title="Lines changed"
                metrics={LOC_METRICS}
                selectedMetrics={selectedLocMetrics}
                onMetricsChange={setSelectedLocMetrics}
                selectedMembers={selectedMembers}
                memberLogins={memberLogins}
                data={data}
                emptyHint="Tick the metric to plot lines changed per member"
            />

            {coderTemplates.length > 0 && (
                <ChartBlock
                    title="Coder usage"
                    metrics={coderMetrics}
                    selectedMetrics={selectedCoderMetrics}
                    onMetricsChange={setSelectedCoderMetrics}
                    selectedMembers={selectedMembers}
                    memberLogins={memberLogins}
                    data={data}
                    allowDecimals
                    emptyHint="Tick Total or a template to plot Coder active time (hours) per member"
                    caption={
                        <>
                            Coder workspace active time in <strong>hours</strong> (live VS Code / SSH / terminal /
                            app connections; idle workspaces don&apos;t count). &ldquo;Total&rdquo; is the sum across
                            templates — the per-template lines are all Coder.
                        </>
                    }
                />
            )}

            <ChartBlock
                title="Curated events"
                metrics={EVENT_METRICS}
                selectedMetrics={selectedEventMetrics}
                onMetricsChange={setSelectedEventMetrics}
                selectedMembers={selectedMembers}
                memberLogins={memberLogins}
                data={data}
                emptyHint="Select at least one member and one event type"
                caption={
                    <>
                        One series per event type. Add or rename types by editing
                        <code> EVENT_TYPES </code>
                        in <code>client/src/team-insights/types.ts</code> and the matching backend enum.
                    </>
                }
            />
        </Space>
    );
};

export default TeamInsightsTimeline;
